/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import {
  AlumniRequest,
  AlumniUploadCsvRequest,
  AlumniUploadCsvResponse,
  Disable2FARequest,
  Disable2FAResponse,
  EmailResponse,
  FolioAccountVerificationRequest,
  PersonalDataRequest,
  Submit2FARequest,
  TokenVerification,
  TwoFactorSecretResponse,
  TwoFactorStatusResponse,
  UserNotificationsSettingsRequest,
  UserPrivacySettingsRequest,
  UsersUploadCsvRequest,
  UsersUploadCsvResponse,
  Validate2FARequest,
} from '../../../shared/models/account/account.model';
import { ObservableResult } from '../../../shared/store';
import { UserDetailsSummary, UserSession } from '../../../user-auth/models';

/** Injection token for the data service, used by angular to decide whether to inject Mock or API service at runtime. */
export const ACCOUNT_DATA_SERVICE = new InjectionToken<AccountDataService>('[Account] DataService');

/** The admin data service. */
export interface AccountDataService {
  getPersonalData(): ObservableResult<UserDetailsSummary>;

  updatePersonalData(userData: PersonalDataRequest): ObservableResult<UserDetailsSummary>;

  resetUserPassword(): ObservableResult<void>;

  resetPasswordByEmail(email: string, captchaResponseV3?: string, captchaResponseV2?: string): ObservableResult<void>;

  getEmailByToken(tokenVerification: TokenVerification): ObservableResult<EmailResponse>;

  resetUserEmail(): ObservableResult<void>;

  resetUserRecoveryEmail(email: string): ObservableResult<void>;

  updateUserPassword(password: string, token: string, captchaResponseV3?: string, captchaResponseV2?: string): ObservableResult<void>;

  updateUserEmail(email: string, token: string, captchaResponseV3?: string, captchaResponseV2?: string): ObservableResult<void>;

  updateUserRecoveryEmail(token: string): ObservableResult<void>;

  updateUserPrivacy(request: UserPrivacySettingsRequest): ObservableResult<void>;

  updateUserNotifications(request: UserNotificationsSettingsRequest): ObservableResult<void>;

  connectUserToWorkspace(email: string): ObservableResult<void>;

  connectUniversityEmail(universityEmail: string, folioAccountInitialization: boolean): ObservableResult<void>;

  leaveUserFromWorkspace(organizationUid: string): ObservableResult<void>;

  verifyWorkspaceEmail(request: FolioAccountVerificationRequest): ObservableResult<void>;

  confirmWorkspaceEmailWithPassword(request: FolioAccountVerificationRequest): ObservableResult<void>;

  alumniRequest(request: AlumniRequest): ObservableResult<void>;

  alumniUploadCsv(request: AlumniUploadCsvRequest): ObservableResult<AlumniUploadCsvResponse>;

  usersUploadCsv(request: UsersUploadCsvRequest): ObservableResult<UsersUploadCsvResponse>;

  setLanguage(code: string): ObservableResult<void>;

  updateJiscUsage(hasUsedCpd: boolean): ObservableResult<void>;

  getUserSessions(): Observable<UserSession[]>;

  get2FAStatus(): ObservableResult<TwoFactorStatusResponse>;

  generate2FASecret(): ObservableResult<TwoFactorSecretResponse>;

  submit2FA(request: Submit2FARequest): ObservableResult<void>;

  validate2FACode(request: Validate2FARequest): ObservableResult<void>;

  disable2FA(request?: Disable2FARequest): ObservableResult<Disable2FAResponse>;
}
